import styled from '@emotion/styled';
import { theme } from '@travelnest/hui';
import { PillButton, PresetThemes } from '@travelnest/merlin.button';
import ProgressArc from '@travelnest/merlin.progress-arc';
import { Spacer } from '@travelnest/merlin.spacer';
import { HeadingSmall, Paragraph } from '@travelnest/merlin.typography';
import React from 'react';
import { Emoji } from '../emoji/Emoji';
import { ScoreInform } from '../score-inform/ScoreInform';

export const paletteV5 = {
	purple: {
		primary: '#7E6EFA',
		purple2: '#A499F3',
	},
	pink: {
		primary: '#FC43F8',
	},
	green: {
		primary: '#56C9B4',
	},
	greyscale: {
		black: '#000000',
		gray1: '#333333',
		gray2: '#4F4F4F',
		gray3: '#888888',
		gray4: '#BDBDBD',
		gray5: '#E0E0E0',
		gray6: '#F2F2F2',
		white: '#FFFFFF'
	}
};

const BASE_FONT = theme.typography.IM100.fontFamily;
export const typographyV5 = {
	fonts: {
		base: BASE_FONT,
		icons: 'TravelNest'
	},
	h1: {
		fontFamily: BASE_FONT,
		fontWeight: 500,
		fontSize: '26px',
		lineHeight: '32px',
		color: paletteV5.greyscale.gray1,
	},
	h2: {
		fontFamily: BASE_FONT,
		fontWeight: 500,
		fontSize: '20px',
		lineHeight: '24px',
		color: paletteV5.greyscale.gray1,
	},
	h3: {
		fontFamily: BASE_FONT,
		fontWeight: 500,
		fontSize: '16px',
		lineHeight: '20px',
		color: paletteV5.greyscale.gray1,
	},
	p: {
		fontFamily: BASE_FONT,
		fontWeight: 400,
		fontSize: '15px',
		lineHeight: '19px',
		color: paletteV5.greyscale.gray2,
	},
	pMed: {
		fontFamily: BASE_FONT,
		fontWeight: 500,
		fontSize: '15px',
		lineHeight: '1.25rem',
		color: paletteV5.greyscale.gray2,
	},
	micro: {
		fontFamily: BASE_FONT,
		fontWeight: 500,
		fontSize: '13px',
		color: paletteV5.greyscale.gray3,
	},
};

const ScoreInfoWrapper = styled.div`
	display: grid;
	grid-template-columns: 135px auto;
	grid-gap: ${theme.spacing.space30};
	align-items: center;
	max-width: 500px;

	.emoji {
		margin-right: ${theme.spacing.space20};
	}

	svg {
		height: 135px;
	}
	@media only screen and (max-width: 360px) {
		display: block;

		svg {
			height: 100px;
			margin-top: unset;
		}
	}
`;

const ScoreInformContentWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	padding: ${theme.spacing.space30};
	font-family: ${typographyV5.micro.fontFamily};
	font-weight: ${typographyV5.micro.fontWeight};
	font-size: ${typographyV5.micro.fontSize};

	div {
		display: flex;
	}

	button {
		max-width: 109px;
	}
`;

const ParagraphWrapper = styled.div`
	@media only screen and (min-width: 768px) {
		max-width: 60%;
	}
`;

/*
Logic here is a bit funky.
Simply put, if a property has unpublished changes or is currently in the process of being publishes
then continue to show the publish changes inform component.
However the scoreDiff param will actually change and cause a rerender some seconds _before_ the
currentlyPublishing param will change due to the "sleep" functionality within the for loop being used
in the publishChanges function so we need to have the extra scoreDiff === 0 conditional as well.
So essentially show publishing unless we are actively not publishing OR we are currently publishing
but we already have the updated score from the service.
*/
const displayScoreInform = (
	propertyId: number,
	hasUnpublishedChanges: boolean,
	scoreDiff: number,
	publishChanges: (propertyId: number) => void,
	currentlyPublishing: boolean,
) => {
	return (
		<div>
			{scoreDiff === 0 && (hasUnpublishedChanges || currentlyPublishing) &&
				<ScoreInform
					backgroundColor={paletteV5.pink.primary}
					color={paletteV5.greyscale.white}
				>
					<ScoreInformContentWrapper>
						<div>
							<Emoji symbol="⚠️" label="warning sign emoji" />
						</div>
						<div style={{ flexDirection: 'column' }}>
							<span>
								Some of the updates to your property need to be published to update your score.
							</span>
							<Spacer spacing="small" />
							<PillButton
								buttonType={PresetThemes.ThemeV5Neutral}
								onClick={() => publishChanges(propertyId)}
								loading={currentlyPublishing}
							>
								Publish now
							</PillButton>
						</div>
					</ScoreInformContentWrapper>
				</ScoreInform>
			}
			{scoreDiff === 0 && (!hasUnpublishedChanges && !currentlyPublishing) &&
				<>
					<Paragraph style={{ ...typographyV5.p }}>Aim for a high score to attract 5X more bookings!</Paragraph>
					<Spacer spacing="small" />
				</>
			}
			{scoreDiff !== 0 && (!hasUnpublishedChanges || !currentlyPublishing) &&
				<ScoreInform
					backgroundColor={scoreDiff > 0 ? paletteV5.green.primary : paletteV5.pink.primary}
					color={scoreDiff > 0 ? paletteV5.greyscale.gray1 : paletteV5.greyscale.white}
				>
					{scoreDiff > 0 ?
						<ScoreInformContentWrapper>
							<div>
								<Emoji symbol="🎉" label="party popper emoji" />
								<div>Great work! You've increased your score!</div>
							</div>
						</ScoreInformContentWrapper>
						:
						<ScoreInformContentWrapper>
							<div>
								<Emoji symbol="😔" label="pensive face emoji" />
								<div>Your score has decreased.</div>
							</div>
						</ScoreInformContentWrapper>
					}
				</ScoreInform>
			}
		</div>
	);
};

interface IPropertyScoreSummaryProps {
	title: string;
	score: number;
	publishChanges: (id: number) => void;
	propertyId: number;
	hasUnpublishedChanges: boolean;
	scoreDiff: number;
	numberOfAttributes: number;
	currentlyPublishing: boolean;
	goal?: number;
	children?: string | React.ReactNode;
}

export const PropertyScoreSummary = ({
	title,
	score,
	propertyId,
	publishChanges,
	hasUnpublishedChanges,
	scoreDiff,
	goal,
	numberOfAttributes,
	currentlyPublishing,
	children
}: IPropertyScoreSummaryProps
) => {
	return (
		<>
			<HeadingSmall style={{ ...typographyV5.h2, color: paletteV5.purple.primary }}>{title}</HeadingSmall>
			<Spacer spacing="small" />
			<ParagraphWrapper>
				<Paragraph style={{ ...typographyV5.p }}>
					Your score is determined by {numberOfAttributes} factors. These affect how your property performs on channels. The higher your score,
					the more bookings you'll receive.
				</Paragraph>
			</ParagraphWrapper>
			{children}
			<Spacer />
			<ScoreInfoWrapper>
				<ProgressArc value={score} change={scoreDiff} goalValue={goal} />
				{displayScoreInform(propertyId, hasUnpublishedChanges, scoreDiff, publishChanges, currentlyPublishing)}
			</ScoreInfoWrapper>
			<Spacer />
		</>
	);
};
